import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User } from '~/components/Shared/User/User';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Text } from '~/eds/src/components/Text/Text';
import { TableContextType } from '~/enums/TableContextType';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { capitalize } from '~/utils/strings';

import { useCreateAction } from '../hooks/useCreateAction';
import { ModelProgress } from '../ModelProgress';
import { TargetEntity } from '../types/entity';
import { ModelActionType, ModelResult, TabKey } from '../types/model';
import {
  testIsActivePublishStatus,
  transformTabKeyToStatus,
} from '../utils/status';
import { ModelResultTargetEntity } from './ModelResultTargetEntity';

interface Props {
  entityType: TargetEntity['type'];
  onShowResult: (modelResult: ModelResult) => void;
  tab?: TabKey;
}

export const ModelResults = ({ entityType, onShowResult, tab }: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const dispatch = useDispatch();
  const enableModelLibrary = useFlag(FlagType.DocumentXRayPrebuiltModels);
  const { getSearchParams, getSearchParam, setSearchParams } = useRouting();
  const searchParam = getSearchParams();
  const tabKey = tab ?? (searchParam.tab as TabKey | undefined);
  const isDeleteTab = tabKey === 'deleted';

  const {
    tableSettings = {
      pageSize: 10,
      sortBy: { id: 'modifiedDate', desc: true },
    },
  } = useTableSettings(TableContextType.FieldAi);

  const pageIndex = Number(getSearchParam('pageIndex') ?? 1);

  const [sortBy, setSortBy] = useState<
    { id: string; desc: boolean } | undefined
  >(tableSettings.sortBy);

  const [pageSize, setPageSize] = useState(tableSettings.pageSize);

  const {
    data: { results, totalCount } = {
      results: [],
      totalCount: 0,
    },
    isError,
    isFetching,
    isLoading,
  } = api.endpoints.getPromptModelResults.useQuery({
    pageIndex,
    pageSize: pageSize,
    status: transformTabKeyToStatus(tabKey),
    sortBy: sortBy,
  });

  const [
    updatePromptModelProgress,
    { isLoading: isUpdatingPromptModelProgress },
  ] = api.endpoints.updatePromptModelProgress.useMutation();

  const handlePaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      setSearchParams({ pageIndex: String(pageIndex) });
    },
    [setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      setPageSize(pageSize);
      setSearchParams({ pageIndex: '1' });
    },
    [setSearchParams],
  );

  const getRowActions = useCallback(
    (modelResult: ModelResult) => {
      const { currentPublishStatus } = modelResult;
      const modelActions = [
        currentPublishStatus === 'suspended'
          ? 'resume'
          : testIsActivePublishStatus(currentPublishStatus)
            ? 'suspend'
            : null,
        'delete',
      ].filter((action) => action !== null) as ModelActionType[];

      const disabled = isFetching || isUpdatingPromptModelProgress;

      return modelActions.map((action) => ({
        key: action,
        disabled,
        label: capitalize(action),
        tooltip: disabled
          ? intl.formatMessage({
              id: 'ModelResults.updatingModel',
              defaultMessage: 'Updating model…',
              description: 'Tooltip when model is being updated',
            })
          : undefined,
        onClick: () => {
          if (action === 'resume') {
            updatePromptModelProgress({ modelId: modelResult.id });
          }
          dispatch(
            slices.xRay.actions.setModelAction({
              model: {
                id: modelResult.id,
                entity: modelResult.targetEntity,
                entityDetails: modelResult.targetEntityDetails,
                name: '',
                version: modelResult.latestVersion,
              },
              action,
            }),
          );
        },
      }));
    },
    [isFetching, isUpdatingPromptModelProgress],
  );

  const hasModels = results.length > 0;

  const createAction = useCreateAction(entityType);

  const emptyContent = {
    action: createAction,
    image: 'empty-no-groups',
    title: intl.formatMessage(
      {
        id: 'ModelResults.buildModelTitle',
        defaultMessage: 'Build a {entityType} Model',
        description:
          'Title for empty state when no models exist. Example: entityType = "document"',
      },
      { entityType: capitalize(entityType) },
    ),
    message: intl.formatMessage({
      id: 'ModelResults.buildModelMessage',
      defaultMessage:
        'Let our AI find the information you are looking for across documents.',
      description: 'Message for empty state when no models exist',
    }),
  };

  const placeholderContent = isError
    ? {
        description: intl.formatMessage({
          id: 'ModelResults.errorLoadingModels',
          defaultMessage:
            'There was an error loading Custom AI models. Refresh the page to try again.',
          description: 'Error message when models fail to load',
        }),
      }
    : !hasModels
      ? emptyContent
      : undefined;

  const isStatusDeleted = isDeleteTab
    ? []
    : [
        {
          key: 'docsInScope',
          align: 'right',
          disableSortBy: true,
          title: intl.formatMessage({
            id: 'ModelResults.documentsInScope',
            defaultMessage: 'Documents in Scope',
            description: 'Column header for documents in scope',
          }),
          width: 'm',
          renderCell: (modelResult: ModelResult) => (
            <ModelProgress metric="docsInScope" modelId={modelResult.id} />
          ),
        },
        {
          key: 'valuesFound',
          align: 'right',
          disableSortBy: true,
          title: intl.formatMessage({
            id: 'ModelResults.valuesFound',
            defaultMessage: 'Values Found',
            description: 'Column header for values found',
          }),
          width: 'm',
          renderCell: (modelResult: ModelResult) => (
            <ModelProgress metric="valuesFound" modelId={modelResult.id} />
          ),
        },
      ];

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ModelResults.model',
        defaultMessage: 'Model',
        description: 'Column header for model name',
      }),
      renderCell: (modelResult: ModelResult) => (
        <ModelResultTargetEntity
          modelResult={modelResult}
          readOnly={isDeleteTab}
        />
      ),
    },
    {
      key: 'scope',
      cellType: 'text',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ModelResults.scope',
        defaultMessage: 'Scope',
        description: 'Column header for model scope',
      }),
      width: 'l',
      renderCell: (modelResult: ModelResult) => (
        <QueryFilters
          mode="preview"
          placeholder="-"
          readOnly
          query={modelResult.scope ?? []}
          onQueryChange={() => {}}
        />
      ),
    },
    {
      key: 'modifiedBy',
      cellType: 'user',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ModelResults.modifiedBy',
        defaultMessage: 'Modified By',
        description: 'Column header for who modified the model',
      }),
      mapCellProps: (modelResult: ModelResult) => ({
        asyncUser: {
          id: modelResult.modifiedBy,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'modifiedDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'ModelResults.modifiedDate',
        defaultMessage: 'Modified Date',
        description: 'Column header for when the model was modified',
      }),
      mapCellProps: (modelResult: ModelResult) => ({
        datetime: modelResult.modifiedDate,
        format: 'short',
        tooltip: modelResult.modifiedDate
          ? formatDate(modelResult.modifiedDate, 'time')
          : undefined,
      }),
    },
    ...isStatusDeleted,
    {
      key: 'publishStatus',
      title: intl.formatMessage({
        id: 'ModelResults.status',
        defaultMessage: 'Status',
        description: 'Column header for model status',
      }),
      disableSortBy: true,
      width: 'm',
      renderCell: (modelResult: ModelResult) => (
        <ModelProgress metric="status" modelId={modelResult.id} />
      ),
    },
    ...(enableModelLibrary
      ? [
          {
            key: 'libraryOrigin',
            cellType: 'icon',
            disableSortBy: true,
            title: intl.formatMessage({
              id: 'ModelResults.libraryColumnHeader',
              defaultMessage: 'Library',
              description: 'Column header for model library origin',
            }),
            width: 's',
            renderCell: (modelResult: ModelResult) => {
              return Boolean(modelResult.libraryModelId) ? (
                <Icon
                  icon="library"
                  tooltip={intl.formatMessage({
                    id: 'ModelResults.libraryIconTooltip',
                    defaultMessage: 'From model library',
                    description: 'Tooltip for models from library icon',
                  })}
                />
              ) : (
                <Text>-</Text>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <ContentContainer
      loadingContent={{
        isLoading,
        message: intl.formatMessage({
          id: 'ModelResults.loadingModels',
          defaultMessage: 'Loading Custom AI models…',
          description: 'Loading message when fetching models',
        }),
      }}
      placeholderContent={placeholderContent}
    >
      <PersistedTable
        columns={columns}
        context={TableContextType.FieldAi}
        data={results}
        getRowActions={isDeleteTab ? undefined : getRowActions}
        name="field-model-list"
        options={{
          enableExportXlsx: false,
          enableSelectRows: false,
        }}
        reactTableOptions={{
          manualSortBy: true,
          autoResetHiddenColumns: false,
          autoResetResize: false,
        }}
        rowDetails={
          isDeleteTab
            ? undefined
            : {
                condition: (modelResult: ModelResult) =>
                  modelResult.versions.length > 0,
                onClick: onShowResult,
              }
        }
        state={{
          pageIndex,
          pageSize,
          sortBy: [sortBy],
        }}
        totalCount={totalCount}
        onPaginate={handlePaginate}
        onPageSizeChange={handlePageSizeChange}
        onUpdate={(state, action) => {
          if (action?.type === 'toggleSortBy') {
            setSortBy(state.sortBy[0]);
          }
        }}
      />
    </ContentContainer>
  );
};
